import { UTCDate } from '@date-fns/utc';
import { subWeeks, subYears } from 'date-fns';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { useUserTrialDetails } from '@revelio/auth';
import { MonthWeekIndexQuery, TrialType } from '@revelio/data-access';

export const useAvailableDateRangeWithTrialLag = () => {
  const { trialType } = useUserTrialDetails();
  const [{ data: dateIndexData, fetching: isLoading }] = useQuery({
    query: MonthWeekIndexQuery,
  });

  const baseAvailableDateRange = useMemo(() => {
    const startWeekStr = dateIndexData?.index?.startWeek;
    const endWeekStr = dateIndexData?.index?.maxWeek;
    const startMonthStr = dateIndexData?.index?.startMonth;
    const endMonthStr = dateIndexData?.index?.endMonth;

    if (!startWeekStr || !endWeekStr || !startMonthStr || !endMonthStr) {
      return null;
    }

    const week = {
      min: new UTCDate(startWeekStr),
      max: new UTCDate(endWeekStr),
    };

    const month = {
      min: new UTCDate(startMonthStr),
      max: new UTCDate(endMonthStr),
    };

    return { week, month };
  }, [dateIndexData]);

  const availableDateRange = useMemo(() => {
    if (isLoading || !baseAvailableDateRange) {
      return null;
    }

    const isLagged = trialType === TrialType.ONE_YEAR_DATA_LAG;

    if (isLagged) {
      try {
        const laggedWeekMax = subWeeks(
          baseAvailableDateRange.week.max,
          53
        ) as UTCDate;
        const laggedMonthMax = subYears(
          baseAvailableDateRange.month.max,
          1
        ) as UTCDate;

        return {
          week: {
            min: baseAvailableDateRange.week.min,
            max: laggedWeekMax,
          },
          month: {
            min: baseAvailableDateRange.month.min,
            max: laggedMonthMax,
          },
        };
      } catch {
        return baseAvailableDateRange;
      }
    } else {
      return baseAvailableDateRange;
    }
  }, [baseAvailableDateRange, isLoading, trialType]);

  return { availableDateRange };
};
